import React, { useState, useEffect, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import { isNullOrEmptyArray } from '../../../../../util/arrays';
import {
  storeMultCrossingConfig,
  updateMultCrossingConfig,
  findMultCrossingConfig,
} from '../../../../../stores/actions/configs';
import { Modal, InputText, QuestionIcon, Loading } from '../../../../../components';
import { usePromptConfirm, useErrorToast } from '../../../../../hooks';
import {
  SingleQuestionAwareCollapsibleList,
  MultiQuestionsAwareCollapsibleList,
} from '../../../../AwareCollapsibleList';
import * as Ui from './styles';

const msgSaveWithSuccess = 'Configuração salva com sucesso';
const stages = {
  WAITING: 0,
  EMPTY: 1,
};

const titleLength = 100;

function MultCrossConfig({
  // identidade do cliente
  customerId,
  // identidade da pesquisa
  surveyId,
  // identidade da pergunta principal
  questionId,
  // enunciado da pergunta principal
  questionTitle,
  // identificador da configuração
  configId,
  // define se o modal está aberto
  isOpen = false,
  // propaga o evento de acionamento de fechar o modal
  onClose,
  // propaga o evento de acionamento de salvar os dados
  onSave,
  // outros propriedades
  ...props
}) {
  // props from redux
  const { dispatch, running, error, multCrossConfigData } = props;
  // prompts
  const [openConfirm, renderConfirm] = usePromptConfirm();

  // state
  const [stage, setStage] = useState(stages.EMPTY);
  const [isShowing, show] = useState(isOpen);
  const [title, setTitle] = useState('');
  const [mainQuestionId, setMainQuestionId] = useState(null);
  const [crossedAltsIds, setCrossedAltsIds] = useState([/* {questionId, answerId} */]);
  const [hiddenOkButton, setHiddenOkButton] = useState(true);
  const [editMode, setEditMode] = useState(false);

  // toast
  const [checkError] = useErrorToast();

  // refs: modal component
  const singleCollapsedListRef = useRef(null);
  const multiCollapsedListRef = useRef(null);

  // efeito de alterar a pergunta
  useEffect(() => {
    if (questionId) {
      setHiddenOkButton(false);
      setMainQuestionId(questionId);
    }
  }, [questionId]);

  // on initialize in edit mode
  useEffect(() => {
    if (multCrossConfigData && editMode && !title) {
      // title
      setTitle(multCrossConfigData.descricao);
      // perguntas_cruzadas
      setCrossedAltsIds(multCrossConfigData.perfil.caracteristicas.map((c) => ({
        questionId: c.pergunta.id, answerId: c.alternativa.id,
      })));
    }
  }, [editMode, multCrossConfigData, title]);

  /**
   * Dispara uma chamada para recuperar os dados para entrar no modo de edição
   */
  useEffect(() => {
    if (configId) {
      findMultCrossingConfig(customerId, surveyId, questionId, configId, dispatch);
      setEditMode(true);
    }
  }, [dispatch, configId, customerId, questionId, surveyId]);

  /**
   * callbacks: reinicia todos os dados
   */
  const resetAll = useCallback(() => {
    setTitle('');
    show(false);
    setMainQuestionId(null);
    setCrossedAltsIds([]);
    setStage(stages.EMPTY);
    if (singleCollapsedListRef.current) {
      singleCollapsedListRef.current.reset();
    }
    if (multiCollapsedListRef.current) {
      multiCollapsedListRef.current.reset();
    }
  }, []);

  /**
   * callbacks: fecha esta configuração
   */
  const close = useCallback((saving, editing) => {
    resetAll();
    // propagar evento de salvar e fechar
    if (saving && onSave) {
      onSave(editing);
    }
    if (!saving && onClose) {
      onClose();
    }
  }, [resetAll, onClose, onSave]);

  // handle on change main alternative
  function handleChangeCrossedAlternatives(qId, itemsIds) {
    // remover qualquer crossedAltsId que contenha questionId === qId
    const oldSel = crossedAltsIds.filter((a) => a.questionId !== qId);
    // adiciona a crossedAltsId se houver itemsIds
    if (!isNullOrEmptyArray(itemsIds)) {
      const sel = [...oldSel, { questionId: qId, answerId: itemsIds[0] }];
      setCrossedAltsIds(sel);
    } else {
      setCrossedAltsIds(oldSel);
    }
  }

  /**
   * save config
   */
  function handleSave() {
    // validar dados
    if (!title || isNullOrEmptyArray(crossedAltsIds)) {
      toast.error(
        'Defina um título e selecione pelo menos uma das alternativas para montar o perfil antes de continuar',
      );
      return;
    }
    // open dialog to storing
    openConfirm(() => {
      // dados da configuração
      const configData = {
        customerId,
        surveyId,
        title,
        profiles: crossedAltsIds,
        questionsIds: [mainQuestionId],
      };
      // ação de salvar ou editar
      if (editMode) {
        updateMultCrossingConfig(configId, configData, dispatch);
      } else {
        storeMultCrossingConfig(configData, dispatch);
      }
      setStage(stages.WAITING);
    });
  }

  // manipula o acionamento direto de mecanismos de fechamento do modal
  function handleCancel() {
    close(false, false);
  }

  function handleChangeTitle(text) {
    setTitle(text);
  }

  /**
   * É responsabilidade deste componente controlar a abertura e fechamento
   * do Modal, logo, é melhor controlar essa abertura através do estado
   * ao invés de delegar para o "parent" através de isOpen.
   *
   * Dessa forma foi criado um isShowing, cujo efeito de alteração em isOpen
   * pode ser percebido por este componente. Ficando, dessa forma, o controle
   * de abertura e fechamento sob responsabilidade deste componente.
   *
   * Seguir este raciocínio é recomendado.
   */
  useEffect(() => {
    show(isOpen);
  }, [isOpen]);

  /**
   * No caso de haver erro depois que solicitar salvar
   */
  useEffect(() => {
    if (stage === stages.WAITING && !running) {
      toast.success(msgSaveWithSuccess);
      close(true, editMode);
    }
  }, [close, editMode, running, stage]);

  // error effect
  useEffect(() => {
    if (error) {
      checkError(error);
    }
  }, [checkError, error]);

  // renderizar uma mensagem de erro
  function renderError() {
    return (
      <span className="errorMessage">
        Para realizar uma configuração é necessário selecionar a pergunta principal
      </span>
    );
  }

  // renderizar um container para trabalho no modal
  function renderContainer() {
    return (
      <Ui.Container>
        <Ui.TitleGroup>
          <Ui.Label>{'Título da Configuração'}</Ui.Label>
          <InputText onChange={handleChangeTitle} value={title} maxLength={titleLength} />
        </Ui.TitleGroup>
        <Ui.ItemsGroup>
          {/*  */}
          <Ui.FixedItem>
            <Ui.Label>
              {'Pergunta principal'}
              <Ui.LabelSmall>
                {'(selecione uma alternativa para cruzar com o perfil)'}
              </Ui.LabelSmall>
            </Ui.Label>
            <SingleQuestionAwareCollapsibleList
              checkedTitle
              boldedTitle
              expansible
              ref={singleCollapsedListRef}
              customerId={customerId}
              surveyId={surveyId}
              questionId={questionId}
              questionTitle={questionTitle}
            />
          </Ui.FixedItem>
          <Ui.DynamicItems>
            <MultiQuestionsAwareCollapsibleList
              expansibleItems
              selectableItems
              ref={multiCollapsedListRef}
              customerId={customerId}
              surveyId={surveyId}
              label={'Selecione as perguntas e alternativas para montar o perfil'}
              onChangeSelectedAlternativesIds={(qId, altsIds) => handleChangeCrossedAlternatives(qId, altsIds)}
              questionsToIgnore={[questionId]}
              initialSelectedQuestionsIds={crossedAltsIds.map((a) => a.questionId)}
              initialSelectedAlternativesIds={crossedAltsIds}
              hiddenCheckboxCollapsibleQuestion={true}
            />
          </Ui.DynamicItems>
        </Ui.ItemsGroup>
      </Ui.Container>
    );
  }

  function renderModalBody() {
    if (running) {
      return (<Loading size={30} running />);
    }
    if (customerId && surveyId && questionId) {
      return renderContainer();
    }
    return renderError();
  }

  return (
    <Ui.Wrapper>
      <Modal
        isLoading={running}
        isOpen={isShowing}
        title={'Configuração de Cruzamentos Múltiplos'}
        okBtnTitle={'Salvar'}
        cancelBtnTitle={'Cancelar'}
        onOk={handleSave}
        onCancel={handleCancel}
        hiddenOkButton={hiddenOkButton}
      >
        { renderModalBody() }
        { renderConfirm(QuestionIcon, 'Deseja salvar esta configuração?') }
      </Modal>
    </Ui.Wrapper>
  );
}

const mapStateToProps = (state) => {
  const configsState = state.configs.multiCrossConfig;
  return {
    running: configsState.running,
    error: configsState.error,
    multCrossConfigData: configsState.payload ? configsState.payload : null,
  };
};

export default connect(mapStateToProps)(MultCrossConfig);
